import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchLegacyProtection from '../../../core/apiHooks/useFetchLegacyProtection';
import { selectCurrentVehicle } from '../../../core/reducers/currentVehicle';
import { selectPrimaryCalculatorPaymentOptions } from '../../../core/reducers/financialCalculators';
import { selectSelectedSubCategory, setSelectedSubCategory } from '../../../core/reducers/ui';
import { useAppSelector } from '../../../core/utils';
import { useFetchCurrentVehiclePayment } from '../../../core/apiHooks/useFetchPayment';
import { useLanguage } from '../../../utils/sitecoreContext';
const langMap = {
    en: 'englishName',
    fr: 'frenchName',
};
const useProtectionData = () => {
    var _a, _b;
    const { t } = useTranslation();
    const lang = useLanguage();
    const langKey = langMap[lang];
    const dispatch = useDispatch();
    const subCategoryLabels = useMemo(() => ({
        ALL_PROTECTIONS: t('Pages.Bap.allProtectionsLabel'),
        ALL_PROTECTIONS_EN: t('Pages.Bap.allProtectionsLabel', { lng: 'en' }),
        MY_SELECTIONS: t('Pages.Bap.mySelectionsLabel'),
        MY_SELECTIONS_EN: t('Pages.Bap.mySelectionsLabel', { lng: 'en' }),
        FEATURED_PROTECTIONS: t('Pages.Bap.featuredLabel'),
        FEATURED_PROTECTIONS_EN: t('Pages.Bap.featuredLabel', { lng: 'en' }),
    }), [t]);
    const { protectionKeys } = useAppSelector(selectCurrentVehicle) || {};
    const { paymentMethod, termAndRate } = useAppSelector(selectPrimaryCalculatorPaymentOptions);
    const selectedProtectionSubCategory = ((_a = useAppSelector(selectSelectedSubCategory)) === null || _a === void 0 ? void 0 : _a.protections) || '';
    const isFeaturedSubCategorySelected = selectedProtectionSubCategory === subCategoryLabels.FEATURED_PROTECTIONS;
    const { data: paymentData, isLoading: isPaymentLoading, isFetching: isPaymentFetching, isSuccess: isPaymentSuccess, error: paymentError, } = useFetchCurrentVehiclePayment();
    const { data: protectionData, isLoading: isProtectionLoading, isFetching: isProtectionFetching, isSuccess: isProtectionSuccess, error: protectionError, } = useFetchLegacyProtection(paymentMethod);
    const isDataLoaded = !isProtectionLoading && !isProtectionFetching && isProtectionSuccess;
    const isDataFailed = !!protectionError;
    const isPaymentFailed = (!isPaymentLoading && !isPaymentFetching && isPaymentSuccess && !paymentData) || !!paymentError;
    const protections = ((_b = protectionData === null || protectionData === void 0 ? void 0 : protectionData.map(item => {
        var _a;
        const paymentProtection = paymentData === null || paymentData === void 0 ? void 0 : paymentData.protections.find(protect => protect.key === item.key);
        const paymentFromTerm = (_a = paymentProtection === null || paymentProtection === void 0 ? void 0 : paymentProtection.terms.find(term => term.Term === termAndRate)) === null || _a === void 0 ? void 0 : _a.FrequencyPayment;
        return {
            ...item,
            defaultAmount: (paymentProtection === null || paymentProtection === void 0 ? void 0 : paymentProtection.defaultAmount) || item.defaultAmount,
            payment: paymentFromTerm || (paymentProtection === null || paymentProtection === void 0 ? void 0 : paymentProtection.frequencyPayment),
        };
    })) === null || _b === void 0 ? void 0 : _b.sort((a, b) => ((a === null || a === void 0 ? void 0 : a.displayOrder) || 0) - ((b === null || b === void 0 ? void 0 : b.displayOrder) || 0))) || [];
    const protectionCategories = protections
        .filter(item => { var _a; return ((_a = item === null || item === void 0 ? void 0 : item.categories) === null || _a === void 0 ? void 0 : _a.length) > 0; })
        .reduce((acc, item) => [...acc, ...item.categories], []);
    const protectionCategoriesCount = protectionCategories.reduce((acc, item) => {
        if (!acc[item[langKey]]) {
            acc[item[langKey]] = 0;
        }
        acc[item[langKey]] += 1;
        return acc;
    }, {});
    const protectionOptions = protectionCategories.reduce((acc, item) => {
        const key = item[langKey];
        if (!acc.find((i) => i.value === key)) {
            acc.push({
                text: `${key} (${protectionCategoriesCount[key]})`,
                value: key,
                gtmName: item.englishName,
                isEmphasized: key === subCategoryLabels.MY_SELECTIONS,
            });
        }
        return acc;
    }, []);
    const featuredProtections = protections.filter(item => item.isFeatured);
    let filteredProtections = [];
    switch (selectedProtectionSubCategory) {
        case subCategoryLabels.ALL_PROTECTIONS:
            filteredProtections = protections;
            break;
        case subCategoryLabels.FEATURED_PROTECTIONS:
            filteredProtections = featuredProtections;
            break;
        case subCategoryLabels.MY_SELECTIONS:
            filteredProtections = protections.filter(item => protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(item.key));
            break;
        default:
            filteredProtections = protections.filter(item => {
                var _a;
                return (_a = item === null || item === void 0 ? void 0 : item.categories) === null || _a === void 0 ? void 0 : _a.find(category => (category === null || category === void 0 ? void 0 : category[langKey]) === selectedProtectionSubCategory);
            });
            break;
    }
    function handleSetSelectedProtectionSubCategory(subCategory) {
        dispatch(setSelectedSubCategory({ protections: subCategory }));
    }
    const defaultSubCategory = useMemo(() => {
        if (featuredProtections && (featuredProtections === null || featuredProtections === void 0 ? void 0 : featuredProtections.length))
            return subCategoryLabels.FEATURED_PROTECTIONS;
        return subCategoryLabels.ALL_PROTECTIONS;
    }, [featuredProtections, subCategoryLabels.ALL_PROTECTIONS, subCategoryLabels.FEATURED_PROTECTIONS]);
    useEffect(() => {
        if (isProtectionSuccess && (!selectedProtectionSubCategory || !filteredProtections.length)) {
            handleSetSelectedProtectionSubCategory(defaultSubCategory);
        }
    }, [defaultSubCategory, isProtectionSuccess, selectedProtectionSubCategory, filteredProtections]);
    return {
        protections: filteredProtections,
        areProtectionsAvailable: !!protections.length,
        isDataLoaded,
        isDataFailed,
        isPaymentFailed,
        protectionOptions: [
            {
                text: `${subCategoryLabels.ALL_PROTECTIONS} (${(protections === null || protections === void 0 ? void 0 : protections.length) || 0})`,
                value: subCategoryLabels.ALL_PROTECTIONS,
                gtmName: subCategoryLabels.ALL_PROTECTIONS_EN,
                isEmphasized: false,
            },
            (featuredProtections === null || featuredProtections === void 0 ? void 0 : featuredProtections.length) && {
                text: `${subCategoryLabels.FEATURED_PROTECTIONS} (${(featuredProtections === null || featuredProtections === void 0 ? void 0 : featuredProtections.length) || 0})`,
                value: subCategoryLabels.FEATURED_PROTECTIONS,
                gtmName: subCategoryLabels.FEATURED_PROTECTIONS_EN,
                isEmphasized: false,
            },
            ...protectionOptions,
            (protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.length) && {
                text: `${subCategoryLabels.MY_SELECTIONS} (${(protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.length) || 0})`,
                value: subCategoryLabels.MY_SELECTIONS,
                gtmName: subCategoryLabels.MY_SELECTIONS_EN,
                isEmphasized: true,
            },
        ].filter(Boolean),
        selectedProtectionSubCategory,
        defaultProtectionSubCategory: defaultSubCategory,
        setProtectionSelectedSubCategory: handleSetSelectedProtectionSubCategory,
        isFeaturedSubCategorySelected,
        protectionSubcategoryLabels: subCategoryLabels,
    };
};
export default useProtectionData;
